import { BoardMeetingState } from "../types/board-meeting";
import {
  BoardMeetingContent,
  BoardMeetingContentState,
  BoardMeetingContentType,
} from "../types/board-meeting-content";

export enum BoardMeetingError {
  NotFound = "BoardMeetingNotFound",
  NotAllowed = "BoardMeetingNotAllowed",
  InvalidTransition = "BoardMeetingInvalidTransition",
  NoCalled = "BoardMeetingNoCalled",
  NoContents = "BoardMeetingNoContents",
  StatementsIncomplete = "BoardMeetingStatementsIncomplete",
  SecretaryMissing = "BoardMeetingSecretaryMissing",
  VerifiersMissing = "BoardMeetingVerifiersMissing",
  SecretaryIsVerifier = "BoardMeetingSecretaryIsVerifier",
  ContentsOpen = "BoardMeetingContentsOpen",
  ConfirmationsMissing = "BoardMeetingConfirmationsMissing",
  ContentClosed = "BoardMeetingContentClosed",
  DecisionMissing = "BoardMeetingDecisionMissing",
  DecisionNotAllowed = "BoardMeetingDecisionNotAllowed",
  UserNotCalled = "BoardMeetingUserNotCalled",
  UserNotMember = "BoardMeetingUserNotMember",
  InvalidOrder = "BoardMeetingInvalidOrder",
}

type RecordLike = { id: string };

// The minimum a meeting needs to carry for the rules below. Both the API
// service and the SPA pass a BoardMeetingWithDetails / WithPeople here.
export type BoardMeetingRuleSubject = {
  state: BoardMeetingState;
  caller: RecordLike;
  called: RecordLike[];
  secretary?: RecordLike;
  verifiers: RecordLike[];
  confirmations: { user: RecordLike }[];
  sections?: { contents: BoardMeetingContent[] }[];
};

export type BoardMeetingRoles = {
  isCaller: boolean;
  isSecretary: boolean;
  isVerifier: boolean;
  isCalled: boolean;
};

export function getBoardMeetingRoles(
  meeting: BoardMeetingRuleSubject,
  userId: string | undefined,
): BoardMeetingRoles {
  return {
    isCaller: userId !== undefined && meeting.caller.id === userId,
    isSecretary: userId !== undefined && meeting.secretary?.id === userId,
    isVerifier: hasUser(meeting.verifiers, userId),
    isCalled: hasUser(meeting.called, userId),
  };
}

const PREPARATION_STATES = [BoardMeetingState.DRAFT, BoardMeetingState.CALL];
const CONCLUSION_STATES = [
  BoardMeetingState.ONGOING,
  BoardMeetingState.CLEANING,
];

export function canViewBoardMeeting(
  meeting: BoardMeetingRuleSubject,
  userId: string | undefined,
): boolean {
  const { isCaller, isCalled } = getBoardMeetingRoles(meeting, userId);
  return isCaller || (isCalled && !PREPARATION_STATES.includes(meeting.state));
}

// Name, date, location and called users
export function canEditBoardMeetingDetails(
  meeting: BoardMeetingRuleSubject,
  userId: string | undefined,
): boolean {
  return (
    meeting.state === BoardMeetingState.DRAFT &&
    getBoardMeetingRoles(meeting, userId).isCaller
  );
}

// Sections, contents, statements and content attachments
export function canEditBoardMeetingAgenda(
  meeting: BoardMeetingRuleSubject,
  userId: string | undefined,
): boolean {
  return canEditBoardMeetingDetails(meeting, userId);
}

// Secretary and verifiers
export function canEditBoardMeetingAssignments(
  meeting: BoardMeetingRuleSubject,
  userId: string | undefined,
): boolean {
  return (
    meeting.state === BoardMeetingState.OPENED &&
    getBoardMeetingRoles(meeting, userId).isCaller
  );
}

export function canEditBoardMeetingAttendance(
  meeting: BoardMeetingRuleSubject,
  userId: string | undefined,
): boolean {
  return (
    (meeting.state === BoardMeetingState.OPENED ||
      meeting.state === BoardMeetingState.ONGOING) &&
    getBoardMeetingRoles(meeting, userId).isCaller
  );
}

// Conclusions, decisions and open/closed state of contents
export function canEditBoardMeetingConclusions(
  meeting: BoardMeetingRuleSubject,
  userId: string | undefined,
): boolean {
  const { isCaller, isSecretary } = getBoardMeetingRoles(meeting, userId);
  return CONCLUSION_STATES.includes(meeting.state) && (isCaller || isSecretary);
}

export function canCommentOnBoardMeeting(
  meeting: BoardMeetingRuleSubject,
  userId: string | undefined,
): boolean {
  return (
    meeting.state !== BoardMeetingState.COMPLETE &&
    canViewBoardMeeting(meeting, userId)
  );
}

export function canConfirmBoardMeeting(
  meeting: BoardMeetingRuleSubject,
  userId: string | undefined,
): boolean {
  return (
    meeting.state === BoardMeetingState.REVIEW &&
    getBoardMeetingRoles(meeting, userId).isVerifier
  );
}

export function canDeleteBoardMeeting(
  meeting: BoardMeetingRuleSubject,
  userId: string | undefined,
): boolean {
  return getBoardMeetingRoles(meeting, userId).isCaller;
}

// The secretary writes the minutes, so they can't also be the one verifying them
export function getBoardMeetingAssignmentError(
  secretaryId: string | undefined,
  verifierIds: string[],
): BoardMeetingError | undefined {
  return secretaryId !== undefined && verifierIds.includes(secretaryId)
    ? BoardMeetingError.SecretaryIsVerifier
    : undefined;
}

export function hasBoardMeetingConfirmed(
  meeting: BoardMeetingRuleSubject,
  userId: string | undefined,
): boolean {
  return meeting.confirmations.some(
    (confirmation) => confirmation.user.id === userId,
  );
}

// Whether the conclusion snapshot from the end of the meeting should be shown
// next to the cleaned version
export function hasBoardMeetingEndedSnapshot(
  state: BoardMeetingState,
): boolean {
  return [
    BoardMeetingState.CLEANING,
    BoardMeetingState.REVIEW,
    BoardMeetingState.COMPLETE,
  ].includes(state);
}

export function getBoardMeetingContentCloseError(
  content: Pick<BoardMeetingContent, "type" | "decision">,
): BoardMeetingError | undefined {
  if (content.type === BoardMeetingContentType.DECISION && !content.decision) {
    return BoardMeetingError.DecisionMissing;
  }
  return undefined;
}

function getContents(meeting: BoardMeetingRuleSubject): BoardMeetingContent[] {
  return (meeting.sections ?? []).flatMap((section) => section.contents);
}

function allContentsClosed(meeting: BoardMeetingRuleSubject): boolean {
  return getContents(meeting).every(
    (content) => content.state === BoardMeetingContentState.CLOSED,
  );
}

type Transition = {
  from: BoardMeetingState;
  to: BoardMeetingState;
  allowed: (roles: BoardMeetingRoles) => boolean;
  precondition?: (
    meeting: BoardMeetingRuleSubject,
  ) => BoardMeetingError | undefined;
};

const byCaller = (roles: BoardMeetingRoles) => roles.isCaller;

const transitions: Transition[] = [
  {
    from: BoardMeetingState.DRAFT,
    to: BoardMeetingState.CALL,
    allowed: byCaller,
    precondition: (meeting) => {
      const contents = getContents(meeting);
      if (meeting.called.length === 0) {
        return BoardMeetingError.NoCalled;
      }
      if (contents.length === 0) {
        return BoardMeetingError.NoContents;
      }
      if (contents.some((content) => content.statement.trim() === "")) {
        return BoardMeetingError.StatementsIncomplete;
      }
      return undefined;
    },
  },
  {
    from: BoardMeetingState.CALL,
    to: BoardMeetingState.DRAFT,
    allowed: byCaller,
  },
  {
    from: BoardMeetingState.CALL,
    to: BoardMeetingState.CALLED,
    allowed: byCaller,
  },
  {
    from: BoardMeetingState.CALLED,
    to: BoardMeetingState.OPENED,
    allowed: byCaller,
  },
  {
    from: BoardMeetingState.OPENED,
    to: BoardMeetingState.ONGOING,
    allowed: byCaller,
    precondition: (meeting) => {
      if (!meeting.secretary) {
        return BoardMeetingError.SecretaryMissing;
      }
      if (meeting.verifiers.length === 0) {
        return BoardMeetingError.VerifiersMissing;
      }
      return getBoardMeetingAssignmentError(
        meeting.secretary.id,
        meeting.verifiers.map((verifier) => verifier.id),
      );
    },
  },
  {
    from: BoardMeetingState.ONGOING,
    to: BoardMeetingState.ENDED,
    allowed: byCaller,
    precondition: (meeting) =>
      allContentsClosed(meeting) ? undefined : BoardMeetingError.ContentsOpen,
  },
  {
    from: BoardMeetingState.ENDED,
    to: BoardMeetingState.CLEANING,
    allowed: byCaller,
  },
  {
    from: BoardMeetingState.CLEANING,
    to: BoardMeetingState.REVIEW,
    allowed: (roles) => roles.isSecretary,
    precondition: (meeting) =>
      allContentsClosed(meeting) ? undefined : BoardMeetingError.ContentsOpen,
  },
  {
    from: BoardMeetingState.REVIEW,
    to: BoardMeetingState.CLEANING,
    allowed: (roles) => roles.isCaller || roles.isSecretary,
  },
  {
    from: BoardMeetingState.REVIEW,
    to: BoardMeetingState.COMPLETE,
    allowed: (roles) => roles.isCaller || roles.isVerifier,
    precondition: (meeting) =>
      meeting.verifiers.every((verifier) =>
        hasBoardMeetingConfirmed(meeting, verifier.id),
      )
        ? undefined
        : BoardMeetingError.ConfirmationsMissing,
  },
];

export type BoardMeetingTransitionOption = {
  state: BoardMeetingState;
  // Set when the user may perform the transition but a precondition is unmet
  blockedBy?: BoardMeetingError;
};

// Transitions out of the current state that this user is allowed to perform
export function getBoardMeetingTransitionOptions(
  meeting: BoardMeetingRuleSubject,
  userId: string | undefined,
): BoardMeetingTransitionOption[] {
  const roles = getBoardMeetingRoles(meeting, userId);
  return transitions
    .filter(
      (transition) =>
        transition.from === meeting.state && transition.allowed(roles),
    )
    .map((transition) => ({
      state: transition.to,
      blockedBy: transition.precondition?.(meeting),
    }));
}

export function getBoardMeetingTransitionError(
  meeting: BoardMeetingRuleSubject,
  to: BoardMeetingState,
  userId: string | undefined,
): BoardMeetingError | undefined {
  const transition = transitions.find(
    (candidate) => candidate.from === meeting.state && candidate.to === to,
  );
  if (!transition) {
    return BoardMeetingError.InvalidTransition;
  }
  if (!transition.allowed(getBoardMeetingRoles(meeting, userId))) {
    return BoardMeetingError.NotAllowed;
  }
  return transition.precondition?.(meeting);
}

function hasUser(users: RecordLike[], userId: string | undefined): boolean {
  return userId !== undefined && users.some((user) => user.id === userId);
}
